import { useMemo, useState } from 'react';
import { AppDialogLayout } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SelectableOption } from '@/components/ui/selectable-option';

export type TemplateOption = {
    id: string;
    title: string;
    description: string;
    text: string;
};

interface TemplatePickerModalContentProps {
    title: string;
    options: TemplateOption[];
    existingContent?: string;
    onCancel: () => void;
    onSelect: (text: string) => void;
    transformText?: (text: string) => string;
    getPreview?: (text: string) => string;
    showOptionPreview?: boolean;
    replaceLabel: string;
    ctaLabel: string;
    cancelLabel: string;
}

const defaultPreview = (text: string) => {
    if (text.length <= 220) return text;
    return `${text.slice(0, 220).trim()}...`;
};

export default function TemplatePickerModalContent({
    title,
    options,
    existingContent,
    onCancel,
    onSelect,
    transformText,
    getPreview,
    showOptionPreview = true,
    replaceLabel,
    ctaLabel,
    cancelLabel,
}: TemplatePickerModalContentProps) {
    const [selectedId, setSelectedId] = useState(options[0]?.id);
    const [allowReplace, setAllowReplace] = useState(false);

    const selectedTemplate = useMemo(
        () => options.find((option) => option.id === selectedId),
        [options, selectedId],
    );

    const resolvedPreview = getPreview || defaultPreview;
    const selectedText = selectedTemplate
        ? transformText
            ? transformText(selectedTemplate.text)
            : selectedTemplate.text
        : '';

    const hasExistingContent = !!existingContent?.trim();
    const isDifferentContent =
        hasExistingContent && selectedText.trim() !== existingContent?.trim();

    return (
        <AppDialogLayout
            title={title}
            footer={
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button variant="outline" onClick={onCancel}>
                        {cancelLabel}
                    </Button>
                    <Button
                        onClick={() => selectedTemplate && onSelect(selectedText)}
                        disabled={isDifferentContent && !allowReplace}
                    >
                        {ctaLabel}
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-6">
                <div className="grid gap-4 lg:grid-cols-[1.1fr_1.4fr]">
                    <div
                        role="radiogroup"
                        aria-label={title}
                        className="max-h-[360px] space-y-3 overflow-y-auto pr-2"
                    >
                        {options.map((option) => {
                            const optionText = transformText
                                ? transformText(option.text)
                                : option.text;

                            return (
                                <SelectableOption
                                    key={option.id}
                                    control="radio"
                                    checked={option.id === selectedId}
                                    onToggle={() => setSelectedId(option.id)}
                                    className="rounded-2xl p-4"
                                    label={<span className="font-semibold">{option.title}</span>}
                                    description={
                                        <>
                                            <span className="block text-sm text-muted-foreground">
                                                {option.description}
                                            </span>
                                            {showOptionPreview && (
                                                <span className="mt-1 block">
                                                    {resolvedPreview(optionText)}
                                                </span>
                                            )}
                                        </>
                                    }
                                />
                            );
                        })}
                    </div>

                    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                        <div className="max-h-[360px] overflow-y-auto rounded-xl bg-surface/60 p-4 text-justify text-sm text-foreground">
                            {selectedText}
                        </div>
                    </div>
                </div>

                {isDifferentContent && (
                    <Alert variant="warning" className="rounded-xl">
                        <label className="flex items-start gap-3">
                            <Checkbox
                                checked={allowReplace}
                                onCheckedChange={() => setAllowReplace((prev) => !prev)}
                                className="mt-0.5"
                            />
                            <span>{replaceLabel}</span>
                        </label>
                    </Alert>
                )}
            </div>
        </AppDialogLayout>
    );
}
